import { assertTaxDefinitionsPreserved, DocumentTaxError, parseDocumentTaxes, resolveLineTaxes } from "../../shared/document-taxes";
import { InvoiceConflictError, InvoiceInputError, type InvoiceDraftInput, type InvoiceDraftRecord, type InvoiceTaxRate } from "./types";

export function validateV1Taxes(input: InvoiceDraftInput) {
  if (input.taxModelVersion !== 1) {
    if (input.taxes || input.lines.some(line => line.taxSet !== undefined)) throw new InvoiceInputError("Tax sets require tax model version 1");
    return;
  }
  try {
    const taxes = parseDocumentTaxes(input.taxes);
    for (const line of input.lines) resolveLineTaxes(taxes, line.taxMode!, line.taxSet ?? []);
  } catch (error) {
    if (error instanceof DocumentTaxError) throw new InvoiceInputError(error.message);
    throw error;
  }
}

export function guardTaxWrite(previous: InvoiceDraftRecord, input: InvoiceDraftInput) {
  validateV1Taxes(input);
  if (previous.taxModelVersion !== 1) return;
  if (input.taxModelVersion !== 1) throw new InvoiceConflictError("This document requires the v1 tax-set contract; legacy edits are not supported");
  try { assertTaxDefinitionsPreserved(previous.taxes!, input.taxes!); }
  catch (error) { if (error instanceof DocumentTaxError) throw new InvoiceInputError(error.message); throw error; }
}

// Compatibility is a read projection only. Recorded issued amounts are never
// recalculated or written back while reading an older snapshot.
export function readDocumentTaxes(record: InvoiceDraftRecord): InvoiceDraftRecord {
  if (record.taxModelVersion === 1) {
    validateV1Taxes(record);
    return record;
  }
  return { ...record, taxModelVersion: null };
}

// Explicit, lossless upgrade projection for a v1 writer. It does not persist or
// alter the legacy record, including its historical line/snapshot representation.
export function upgradeLegacyTaxModel(record: InvoiceDraftRecord, defaultRate?: InvoiceTaxRate | null): InvoiceDraftRecord {
  if (record.taxModelVersion === 1) return readDocumentTaxes(record);
  const rates = new Map(record.lines.flatMap(line => line.taxRate ? [[line.taxRate.id, line.taxRate] as const] : []));
  if (defaultRate && !rates.has(defaultRate.id)) rates.set(defaultRate.id, defaultRate);
  const definitions = [...rates.values()].map(rate => ({ id: rate.id, name: rate.label, rateBasisPoints: rate.rateBasisPoints, taxNumber: rate.taxNumber ?? null, savedRateId: rate.id }));
  const byId = new Map(definitions.map(tax => [tax.id, tax]));
  const defaultId = record.defaultTaxRateId;
  if (defaultId && !byId.has(defaultId)) throw new InvoiceInputError("Resolve the legacy document default before upgrading its tax model");
  return {
    ...record, taxModelVersion: 1,
    // Freeze resolved legacy rates on their lines. A legacy document has no
    // v1 default set yet, so formerly untaxed lines can inherit future defaults.
    taxes: { definitions, defaultTaxSet: [] },
    lines: record.lines.map(line => {
      const rate = line.taxRate ?? (line.taxRateId ? rates.get(line.taxRateId) : null);
      if (line.taxRateId && !rate) throw new InvoiceInputError("Resolve the legacy line tax before upgrading its tax model");
      return {
        ...line, taxMode: rate ? "override" : "inherit",
        taxSet: rate ? [rate.id] : [],
        taxBreakdown: rate ? [{ definition: byId.get(rate.id)!, taxableBaseMinor: line.taxableBaseMinor, amountMinor: line.taxAmountMinor }] : [],
      };
    }),
  };
}
